import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import { LeaderboardType } from '@racemap/utilities/consts/events';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Spin } from 'antd';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LeaderboardFooter } from 'src/components/Leaderboard/LeaderboardFooter';
import { LeaderboardHelmet } from 'src/components/Leaderboard/LeaderboardHelmet';
import {
  checkLeaderboardActivationState,
  useLeaderboardEnvironmentInfo,
} from 'src/components/Leaderboard/utils';
import { useIsPageVisible } from 'src/lib/customHooks/useIsPageVisible';
import { getLeaderboardUrl } from 'src/lib/getLeaderboardUrl';
import {
  useIsFirstLoad,
  useIsLoading,
  useLeaderboardActions,
  useParentEventData,
  useRelevantEventsData,
  useSelectedEventData,
  useSelectedEventId,
} from 'src/store/leaderboard/leaderboard_reducer';
import { useInterval } from 'usehooks-ts';
import { NotActivatedOverlay } from '../components/BasicComponents/NotActivatedOverlay/NotActivatedOverlay';
import { Header } from '../components/Leaderboard/Header';
import { Leaderboard } from '../components/Leaderboard/Leaderboard/Leaderboard';
import { LeaderboardErrorPage } from './LeaderboardErrorPage';

type Params = {
  eventSlug: string;
};

const RELOAD_INTERVAL = 1 * OneMinuteInMillis;

export function LeaderboardView() {
  const { eventSlug } = useParams<Params>();

  const relevantEvents = useRelevantEventsData();
  const selectedEventId = useSelectedEventId();
  const loading = useIsLoading();
  const isFirstLoad = useIsFirstLoad();
  const selectedEventData = useSelectedEventData();
  const parentEventData = useParentEventData();
  const { isMobile, isEmbedded } = useLeaderboardEnvironmentInfo();
  const selectedEvent = selectedEventData?.eventObject;
  const parentEvent = parentEventData?.eventObject;

  const { loadEventData, changeSelectedEvent, registerLoad } = useLeaderboardActions();
  const isTabVisible = useIsPageVisible();

  const compact = isEmbedded || isMobile;
  const [, endTime] = selectedEvent != null ? currentEventTimes(selectedEvent) : [];
  const eventIsOver = endTime != null && endTime < Date.now();
  const loadInterval = isTabVisible && !eventIsOver ? RELOAD_INTERVAL : null;
  const navigate = useNavigate();
  const { isArchived, isModuleActivated, isEventAuthorized } = checkLeaderboardActivationState(
    selectedEvent || null,
  );

  useEffect(() => {
    if (eventSlug == null) return;
    loadEventData(eventSlug);
  }, [eventSlug]);

  useEffect(() => {
    const event = Array.from(relevantEvents.values()).find((e) => e.eventObject.slug === eventSlug);
    if (event != null) {
      changeSelectedEvent(event.eventObject.id);
    } else if (relevantEvents.size > 0) {
      const firstEvent = relevantEvents.values().next().value;
      if (firstEvent == null) return;

      changeSelectedEvent(firstEvent.eventObject.id);
    }

    registerLoad();
  }, [relevantEvents]);

  useEffect(() => {
    const slugOfSelectedEvent = selectedEvent?.slug;
    // in case that the event slug is an object id, but the event has a proper slug, we redirect to the proper slug
    if (
      selectedEvent != null &&
      slugOfSelectedEvent != null &&
      ObjectId.isValid(eventSlug || '') &&
      slugOfSelectedEvent !== eventSlug
    ) {
      navigate(getLeaderboardUrl(selectedEvent, { onlyPath: true }), { replace: true });
    }
  }, [selectedEventData, eventSlug]);

  useInterval(() => eventSlug && loadEventData(eventSlug), loadInterval);

  const handleSelectedEventChange = (newEventId: string, newSlug?: string) => {
    navigate(
      getLeaderboardUrl({ id: newEventId, slug: newSlug } as RacemapEvent, { onlyPath: true }),
    );
  };

  // If we land on this page but set a external in the config
  if (
    selectedEvent != null &&
    selectedEvent.modules.timing.leaderboard.type !== LeaderboardType.DEFAULT
  ) {
    // trigger a full page reload, so that gears-api can delegate to the external leaderboard
    window.location.href = getLeaderboardUrl(selectedEvent);
  }

  return (
    <Spin spinning={loading} style={{ height: '100vh' }}>
      <Container>
        <LeaderboardHelmet />
        <Header
          events={Array.from(relevantEvents.values()).map((e) => e.eventObject)}
          selectedEventId={selectedEventId}
          parentEvent={parentEvent}
          onSelectedEventChange={handleSelectedEventChange}
          loading={loading}
        />
        {isFirstLoad || (!isArchived && isModuleActivated) ? (
          <LeaderBoardContainer compact={compact}>
            <Leaderboard />
            {!compact && !isFirstLoad && <LeaderboardFooter />}
          </LeaderBoardContainer>
        ) : (
          <LeaderboardErrorPage event={selectedEvent} />
        )}

        {!isEventAuthorized && !isArchived && isModuleActivated && (
          <NotActivatedOverlay
            className={TestSelectors.leaderboard.mapNotActivatedOverlay.toString()}
          />
        )}
      </Container>
    </Spin>
  );
}

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-size: medium;
`;

const LeaderBoardContainer = styled.div<{ compact: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px;

  @media screen and (min-width: 576px) {
    padding-left: 30px;
    padding-right: 30px;
  }

  @media screen and (min-height: 800px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  ${({ compact }) => compact && 'padding: 0 !important;'}
`;
